import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import Button from 'components/Button';
import FormCheckbox from 'components/FormCheckbox';
import Modal from 'components/Modal';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import API_ENDPOINTS from 'utils/api/endpoints';
import { downloadFile, formatCurrency, toastStyle } from 'utils/helper';

import './styles.scss';

const localDateInputValue = date => {
  const value = date instanceof Date ? date : new Date(date);
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const today = () => localDateInputValue(new Date());

const nextLocalDateLabel = value => {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + 1);
  return date.toLocaleDateString();
};

const errorMessage = error => {
  const data = error?.response?.data;
  if (typeof data?.detail === 'string') return data.detail;
  if (typeof data?.period_end === 'string') return data.period_end;
  if (Array.isArray(data?.period_end)) return data.period_end[0];
  return error?.message || 'Unable to prepare the cost report.';
};

const REPORT_TYPE_OPTIONS = [
  { value: 'both', label: 'Summary + Detail' },
  { value: 'summary', label: 'Summary' },
  { value: 'detail', label: 'Detail' },
];

const warningCards = warnings => [
  {
    key: 'actuals',
    label: warnings?.has_actuals ? 'Actuals found' : 'No actuals entered',
    tone: warnings?.has_actuals ? 'positive' : 'warning',
  },
  {
    key: 'uncoded',
    label: `${warnings?.uncoded_lines || 0} uncoded lines`,
    tone: warnings?.uncoded_lines ? 'warning' : 'positive',
  },
  {
    key: 'forecast',
    label: `${warnings?.manual_etc_overrides || 0} forecast overrides`,
    tone: 'neutral',
  },
  {
    key: 'variance',
    label: `${warnings?.over_budget_lines || 0} over-budget lines`,
    tone: warnings?.over_budget_lines ? 'negative' : 'positive',
  },
  ...(warnings?.new_unbudgeted_lines
    ? [
        {
          key: 'unbudgeted',
          label: `${warnings.new_unbudgeted_lines} new unbudgeted lines`,
          tone: 'warning',
        },
      ]
    : []),
  ...(warnings?.contractual_charges
    ? [
        {
          key: 'contractual',
          label: `${warnings.contractual_charges} contractual charges not actualized`,
          tone: 'neutral',
        },
      ]
    : []),
];

const CostReportModal = () => {
  const {
    selectedBudget,
    budgetData,
    hideCostReportModal,
    hasEditAccess,
    refetchBudgets,
    refetchBudgetId,
    refetchTabs,
  } = useHomeScreenContext();
  const budgetId = selectedBudget?.budget;
  const currency =
    selectedBudget?.primary_currency || budgetData?.primary_currency || 'USD';
  const [activeTab, setActiveTab] = useState('create');
  const [step, setStep] = useState('setup');
  const [periodEnd, setPeriodEnd] = useState(today);
  const [reportType, setReportType] = useState('both');
  const [includeZeroLines, setIncludeZeroLines] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('pdf');
  const [pageSize, setPageSize] = useState('letter');
  const [confirmBaseline, setConfirmBaseline] = useState(false);
  const [replaceLatest, setReplaceLatest] = useState(false);
  const [history, setHistory] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const activeReports = useMemo(
    () => (history?.reports || []).filter(report => report.status === 'active'),
    [history]
  );
  const latestActive = useMemo(
    () =>
      [...activeReports].sort((left, right) =>
        right.period_end.localeCompare(left.period_end)
      )[0],
    [activeReports]
  );
  const isReplacingLatest = Boolean(
    latestActive && latestActive.period_end === periodEnd
  );
  const canEdit = history?.can_edit ?? hasEditAccess?.() ?? false;

  const loadHistory = useCallback(async () => {
    if (!budgetId) return;
    try {
      const response = await API_ENDPOINTS.budgets.get_cost_reports(budgetId);
      setHistory(response.data);
      const latest = (response.data?.reports || [])
        .filter(report => report.status === 'active')
        .sort((left, right) =>
          right.period_end.localeCompare(left.period_end)
        )[0];
      setReplaceLatest(Boolean(latest?.period_end === today()));
    } catch (error) {
      toast.error(errorMessage(error), toastStyle);
    }
  }, [budgetId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const resetPreview = () => {
    setStep('setup');
    setPreview(null);
    setConfirmBaseline(false);
  };

  const closeModal = () => {
    resetPreview();
    hideCostReportModal();
  };

  const requestPayload = {
    budget_id: budgetId,
    period_end: periodEnd,
    report_type: reportType,
    include_zero_lines: includeZeroLines,
    replace_latest: isReplacingLatest && replaceLatest,
  };

  const handlePreview = async () => {
    setIsLoading(true);
    try {
      const response = await API_ENDPOINTS.budgets.preview_cost_report(
        requestPayload
      );
      setPreview(response.data);
      setConfirmBaseline(response.data?.baseline_exists);
      setStep('preview');
    } catch (error) {
      toast.error(errorMessage(error), toastStyle);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (reportId, format = downloadFormat) => {
    try {
      const response = await API_ENDPOINTS.budgets.download_cost_report({
        reportId,
        format,
        pageSize,
      });
      downloadFile(response.data, {
        contentDispositionHeader: response.headers?.['content-disposition'],
        contentTypeHeader: response.headers?.['content-type'],
        fileName: `${selectedBudget?.name || 'Budget'}-Cost-Report.${format}`,
      });
    } catch (error) {
      toast.error(errorMessage(error), toastStyle);
    }
  };

  const handleIssue = async () => {
    if (!preview?.baseline_exists && !confirmBaseline) {
      toast.error(
        'Confirm the approved budget baseline before issuing.',
        toastStyle
      );
      return;
    }
    setIsLoading(true);
    try {
      const response = await API_ENDPOINTS.budgets.create_cost_report({
        ...requestPayload,
        confirm_baseline: confirmBaseline,
      });
      await handleDownload(response.data.id);
      await loadHistory();
      refetchBudgets?.();
      refetchBudgetId?.();
      refetchTabs?.();
      toast.success('Cost report issued successfully.', toastStyle);
      setActiveTab('history');
      resetPreview();
    } catch (error) {
      toast.error(errorMessage(error), toastStyle);
    } finally {
      setIsLoading(false);
    }
  };

  const previewRows = useMemo(() => {
    const data = preview?.data;
    if (!data) return [];
    return reportType === 'detail' ? data.detail_rows : data.summary_rows;
  }, [preview, reportType]);

  const renderSetup = () => (
    <div className="cost-report__setup">
      {!history?.baseline_exists && (
        <div className="cost-report__baseline-callout">
          <strong>Start cost reporting</strong>
          <span>
            Your first issued report locks the current budget as the approved
            baseline. Later budget edits will not rewrite historical variance.
          </span>
        </div>
      )}
      <div className="cost-report__form-grid">
        <label className="cost-report__field">
          <span>Period ending</span>
          <input
            type="date"
            value={periodEnd}
            max={today()}
            min={latestActive?.period_end}
            onChange={event => {
              setPeriodEnd(event.target.value);
              setReplaceLatest(event.target.value === latestActive?.period_end);
            }}
          />
          <small>
            {latestActive
              ? `Starts ${nextLocalDateLabel(latestActive.period_end)}`
              : 'First report starts at inception'}
          </small>
        </label>
        <label className="cost-report__field">
          <span>Report contents</span>
          <select
            value={reportType}
            onChange={event => setReportType(event.target.value)}
          >
            {REPORT_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="cost-report__field">
          <span>Download format</span>
          <select
            value={downloadFormat}
            onChange={event => setDownloadFormat(event.target.value)}
          >
            <option value="pdf">PDF</option>
            <option value="csv">CSV</option>
          </select>
        </label>
        <label className="cost-report__field">
          <span>PDF page size</span>
          <select
            value={pageSize}
            onChange={event => setPageSize(event.target.value)}
            disabled={downloadFormat !== 'pdf'}
          >
            <option value="letter">Landscape Letter</option>
            <option value="ledger">Landscape Ledger</option>
          </select>
        </label>
      </div>
      <FormCheckbox
        id="cost-report-zero-lines"
        label="Include all-zero lines"
        checked={includeZeroLines}
        onChange={event => setIncludeZeroLines(event.target.checked)}
      />
      {isReplacingLatest && (
        <div className="cost-report__replace-callout">
          <FormCheckbox
            id="cost-report-replace"
            label="Replace the latest report for this date"
            checked={replaceLatest}
            onChange={event => setReplaceLatest(event.target.checked)}
          />
          <small>The existing version remains in the audit history.</small>
        </div>
      )}
      <div className="cost-report__time-note">
        This report snapshots the Actual values currently entered in the budget.
        Actuals are cumulative, so the period amount is the change since the
        last issued report.
      </div>
    </div>
  );

  const renderPreview = () => {
    const totals = preview?.data?.totals || {};
    const warnings = preview?.data?.warnings || {};
    return (
      <div className="cost-report__preview">
        <div className="cost-report__metric-grid">
          <div>
            <span>Baseline budget</span>
            <strong>
              {formatCurrency(totals.baseline_budget || 0, currency)}
            </strong>
          </div>
          <div>
            <span>Actual to date</span>
            <strong>
              {formatCurrency(totals.actual_to_date || 0, currency)}
            </strong>
          </div>
          <div>
            <span>Estimated final cost</span>
            <strong>
              {formatCurrency(totals.estimated_final_cost || 0, currency)}
            </strong>
          </div>
          <div
            className={Number(totals.variance || 0) < 0 ? 'is-negative' : ''}
          >
            <span>Variance</span>
            <strong>{formatCurrency(totals.variance || 0, currency)}</strong>
          </div>
        </div>
        <div className="cost-report__readiness">
          {warningCards(warnings).map(card => (
            <span
              key={card.key}
              className={`cost-report__readiness-chip is-${card.tone}`}
            >
              {card.label}
            </span>
          ))}
        </div>
        {!preview?.baseline_exists && (
          <div className="cost-report__confirm-baseline">
            <FormCheckbox
              id="cost-report-confirm-baseline"
              checked={confirmBaseline}
              onChange={event => setConfirmBaseline(event.target.checked)}
              label="I confirm the current budget as the approved cost-report baseline"
            />
          </div>
        )}
        <div className="cost-report__table-wrap">
          <table className="cost-report__table">
            <thead>
              <tr>
                <th>Account #</th>
                <th>Description</th>
                <th>Period</th>
                <th>Actual TD</th>
                <th>ETC</th>
                <th>EFC</th>
                <th>Budget</th>
                <th>Variance</th>
              </tr>
            </thead>
            <tbody>
              {previewRows.slice(0, 18).map(row => (
                <tr
                  key={row.key}
                  className={`is-${(row.row_type || 'detail').replaceAll(
                    '_',
                    '-'
                  )}`}
                >
                  <td>{row.account_number || ''}</td>
                  <td
                    style={{ paddingLeft: `${12 + (row.depth || 0) * 12}px` }}
                  >
                    {row.description}
                  </td>
                  {[
                    'actual_this_period',
                    'actual_to_date',
                    'estimate_to_complete',
                    'estimated_final_cost',
                    'baseline_budget',
                    'variance',
                  ].map(key => (
                    <td
                      key={key}
                      className={
                        key === 'variance' && Number(row[key] || 0) < 0
                          ? 'is-negative'
                          : ''
                      }
                    >
                      {['category_header', 'section_header'].includes(
                        row.row_type
                      )
                        ? ''
                        : row.is_contractual &&
                          ['actual_this_period', 'actual_to_date'].includes(key)
                        ? '—'
                        : formatCurrency(row[key] || 0, currency)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {previewRows.length > 18 && (
            <div className="cost-report__table-more">
              Previewing 18 of {previewRows.length} rows
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="cost-report__history">
      {!history?.reports?.length ? (
        <div className="cost-report__empty">
          No cost reports have been issued yet.
        </div>
      ) : (
        history.reports.map(report => (
          <div
            key={report.id}
            className={`cost-report__history-row is-${report.status}`}
          >
            <div>
              <strong>
                {report.period_start
                  ? `${new Date(
                      `${report.period_start}T12:00:00`
                    ).toLocaleDateString()} – `
                  : 'Inception – '}
                {new Date(`${report.period_end}T12:00:00`).toLocaleDateString()}
              </strong>
              <span>
                {report.generated_by || 'Unknown user'} ·{' '}
                {new Date(report.generated_on).toLocaleString()}
                {report.status === 'superseded' ? ' · Superseded' : ''}
              </span>
            </div>
            <div className="cost-report__history-totals">
              <span>
                EFC{' '}
                {formatCurrency(
                  report.totals?.estimated_final_cost || 0,
                  currency
                )}
              </span>
              <span
                className={
                  Number(report.totals?.variance || 0) < 0 ? 'is-negative' : ''
                }
              >
                Variance{' '}
                {formatCurrency(report.totals?.variance || 0, currency)}
              </span>
            </div>
            <div className="cost-report__history-actions">
              <button
                type="button"
                onClick={() => handleDownload(report.id, 'pdf')}
              >
                PDF
              </button>
              <button
                type="button"
                onClick={() => handleDownload(report.id, 'csv')}
              >
                CSV
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const footer = (
    <div className="cost-report__footer">
      <Button label="Cancel" variant="red-outlined" onClick={closeModal} />
      {activeTab === 'create' && step === 'preview' && (
        <Button label="Back" variant="secondary" onClick={resetPreview} />
      )}
      {activeTab === 'create' && step === 'setup' && (
        <Button
          label="Preview Report"
          onClick={handlePreview}
          isDisabled={
            isLoading || !periodEnd || (isReplacingLatest && !replaceLatest)
          }
        />
      )}
      {activeTab === 'create' && step === 'preview' && (
        <Button
          label={`Issue & Download ${downloadFormat.toUpperCase()}`}
          onClick={handleIssue}
          isDisabled={
            isLoading ||
            !canEdit ||
            (!preview?.baseline_exists && !confirmBaseline)
          }
        />
      )}
    </div>
  );

  return (
    <Modal
      show
      onHide={closeModal}
      size="xl"
      centered
      scrollable
      className="cost-report"
      title={
        <div>
          <div className="cost-report__title">Cost Report</div>
          <div className="cost-report__subtitle">
            {selectedBudget?.name || budgetData?.name}
          </div>
        </div>
      }
      footer={footer}
    >
      <div className="cost-report__tabs">
        <button
          type="button"
          className={activeTab === 'create' ? 'is-active' : ''}
          onClick={() => setActiveTab('create')}
        >
          Create Report
        </button>
        <button
          type="button"
          className={activeTab === 'history' ? 'is-active' : ''}
          onClick={() => setActiveTab('history')}
        >
          History
          {history?.reports?.length ? ` (${history.reports.length})` : ''}
        </button>
      </div>
      {activeTab === 'history'
        ? renderHistory()
        : step === 'preview'
        ? renderPreview()
        : renderSetup()}
    </Modal>
  );
};

export default CostReportModal;
